import { useEffect, useState } from "react";
import { createIndex, createKnowledgeSpaceSource, updateIndex } from "../api/agentApi";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Toolbar, 
  Divider, 
  Paper,
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Stack,
  MenuItem
} from "@mui/material";
import RAGFileUploader from "./RAGFileUploader";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { alpha, useTheme } from "@mui/material/styles";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";

const formControlSx = (colors) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
    bgcolor: colors.buttonSurface,
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: BRAND_COLORS.primary.border },
    "&.Mui-focused fieldset": { borderColor: colors.copper },
  },
});

const modeButtonSx = (active, colors, theme) => ({
  justifyContent: "flex-start",
  borderRadius: UI_RADIUS.button,
  px: 2,
  py: 1.2,
  fontWeight: 900,
  textTransform: "none",
  bgcolor: active ? colors.copper : colors.buttonSurface,
  color: active ? theme.palette.primary.contrastText : colors.copperDark,
  borderColor: colors.border,
  boxShadow: active ? `0 10px 24px ${BRAND_COLORS.primary.shadow}` : "none",
  "&:hover": {
    bgcolor: active ? colors.copperDark : colors.copperSoft,
    borderColor: colors.copper,
    boxShadow: active ? `0 14px 30px ${BRAND_COLORS.primary.shadowHover}` : "none",
  },
});

export function CodeIndexerPage() {
  const [sourcePath, setSourcePath] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleIndex = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      // Split comma-separated paths, trim, filter out empty strings, sort, and remove duplicates
      const pathArray = Array.from(new Set(sourcePath.split(",").map(p => p.trim()).filter(Boolean))).sort();
      const res = await createIndex({
        name: name || `Code Collection - ${pathArray.join(", ")}`,
        description,
        path: pathArray,
        type: "code"
      });
      setResult(
        <span>
          {res.message || "Processing complete."}
          {res.index_id && (
            <><br /><a href={`/kb/code/${res.index_id}`}>View collection status</a></>
          )}
        </span>
      );
    } catch (e) {
      setError(e.message || "Failed to index codebase.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4" sx={{ pl: 0 }}>Code Collection</Typography>
      </Toolbar>
      <Divider sx={{ mb: 3 }} />
      <Box component={"form"} sx={{ mx: "auto", py: 4 }}>
        <TextField label="Index Name" value={name} onChange={e => setName(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <TextField label="Source Path(s) (supports comma-deparated list)" value={sourcePath} onChange={e => setSourcePath(e.target.value)} fullWidth sx={{ mb: 2 }} />
        <Button variant="contained" color="primary" onClick={handleIndex} disabled={loading || !sourcePath} sx={{ mb: 2 }}>
          {loading ? "Processing..." : "Collection"}
        </Button>
        {result && <Alert severity="success" sx={{ mt: 2 }}>{result}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    </Box>
  );
}

export function DocumentIndexerPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = {
    ink: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    border: theme.palette.divider,
    line: theme.palette.divider,
    copper: theme.palette.primary.main,
    copperDark: isDark ? "#F2C69B" : "#935C29",
    copperSoft: isDark ? alpha(theme.palette.primary.main, 0.16) : "rgba(184, 115, 51, 0.10)",
    panel: theme.palette.background.paper,
    buttonSurface: isDark ? alpha("#FFF7EF", 0.06) : "#fff",
  };
  const [sourceMode, setSourceMode] = useState("documents");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [sourceForm, setSourceForm] = useState({
    display_name: "",
    source_type: "tool_query",
    tool_name: "",
    capability: "semantic_search",
  });
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [sourcePathArray, setSourcePathArray] = useState([]);
  const [validationError, setValidationError] = useState({});
  // Track created index to enable updates
  const [createdIndexId, setCreatedIndexId] = useState(null);
  const [indexDetails, setIndexDetails] = useState(null);
  // Track upload ID to reuse for same index
  const [uploadId, setUploadId] = useState(null);
  // Progress tracking
  const [activeStep, setActiveStep] = useState(-1); // -1 means not started
  const [uploadComplete, setUploadComplete] = useState(false);
  
  const steps = ["Upload", "Process Files"];
  
  // Auto-index when files are uploaded
  useEffect(() => {
    if (uploadedFiles && Object.keys(uploadedFiles).length) {
      // Mark upload as complete
      setUploadComplete(true);
      setActiveStep(0); // Move to step 0 (Upload Files)
      
      // get source paths for indexing - extract directory paths
      const paths = uploadedFiles?.uploaded?.map((file) => {
        // Extract directory path from file path
        const filePath = file.path;
        const lastSlashIndex = filePath.lastIndexOf('/');
        return lastSlashIndex > 0 ? filePath.substring(0, lastSlashIndex) : filePath;
      });
      // Remove duplicates since multiple files might be in the same directory
      const uniquePaths = Array.from(new Set(paths));
      if (paths && uniquePaths.length > 0) {
        // Accumulate paths instead of replacing them
        setSourcePathArray(prev => {
          const combinedPaths = [...prev, ...uniquePaths];
          return Array.from(new Set(combinedPaths)); // Remove duplicates
        });
        
        // Automatically trigger indexing after upload
        handleIndexAfterUpload(uniquePaths);
      }
    } 
  },[uploadedFiles])

  const handleIndexAfterUpload = async (newPaths) => {
    // Validate name is provided
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationError(errors);
      setError("Collection name is required. Please provide a name before uploading files.");
      setActiveStep(-1);
      setUploadComplete(false);
      return;
    }
    
    setLoading(true);
    setError("");
    setResult(null);
    setActiveStep(1); // Move to step 1 (Index Files)
    
    try {
      // Combine with existing paths and remove duplicates
      const combinedPaths = [...sourcePathArray, ...newPaths];
      const pathArray = Array.from(new Set(combinedPaths.map(p => p.trim()).filter(Boolean))).sort();
      
      let res;
      if (createdIndexId && indexDetails) {
        // Update existing index
        res = await updateIndex(createdIndexId, {
          source_path: pathArray,
        });
        setResult(
          <span>
            Collection update queued. The files will appear as completed when processing finishes.
            <br /><a href={`/kb/doc/${createdIndexId}`}>View collection status</a>
          </span>
        );
      } else {
        // Create new index
        res = await createIndex({
          name: name || `Doc Index - ${pathArray.join(", ")}`,
          description,
          path: pathArray,
          type: "doc"
        });
        
        // Store the created index info for future updates
        if (res.index_id) {
          setCreatedIndexId(res.index_id);
          setIndexDetails({
            source_path: pathArray.map(path => ({ path })),
            name: name || `Doc Index - ${pathArray.join(", ")}`,
            type: "doc",
            status: "pending"
          });
        }
        
        setResult(
          <span>
            {res.message || "Processing complete."}
            {res.index_id && (
              <><br /><a href={`/kb/doc/${res.index_id}`}>View collection status</a></>
            )}
          </span>
        );
      }
      
      setUploadedFiles({});
      setValidationError({});
      setActiveStep(2); // Complete - beyond the last step
    } catch (e) {
      setError(e.message || "Failed to process files.");
      setActiveStep(-1);
      setUploadComplete(false);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    return newErrors;
  };

  const validateLiveSource = () => {
    const newErrors = validate();
    if (!sourceForm.tool_name.trim()) {
      newErrors.tool_name = "Tool name is required";
    }
    return newErrors;
  };

  const handleCreateLiveSource = async () => {
    const errors = validateLiveSource();
    if (Object.keys(errors).length > 0) {
      setValidationError(errors);
      setError("Add a Knowledge Space name and a published tool function name.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    try {
      const created = await createIndex({
        name: name.trim(),
        description,
        path: [],
        type: "doc",
      });
      const spaceId = created?.index_id;
      if (!spaceId) {
        throw new Error("Knowledge Space was created without an id.");
      }

      await createKnowledgeSpaceSource(spaceId, {
        display_name: sourceForm.display_name.trim() || sourceForm.tool_name.trim(),
        source_type: sourceForm.source_type,
        query_mode: "live",
        status: "ready",
        adapter: "tool_query",
        backing_ref: {
          tool_name: sourceForm.tool_name.trim(),
          argument_map: { query: "query" },
        },
        capabilities: sourceForm.capability.trim() ? [sourceForm.capability.trim()] : ["semantic_search"],
        freshness_policy: "live",
        permission_scope: "tenant",
      });

      setCreatedIndexId(spaceId);
      setIndexDetails({
        source_path: [],
        name: name.trim(),
        type: "doc",
        status: "ready",
      });
      setValidationError({});
      setResult(
        <span>
          Live Knowledge Space created with source "{sourceForm.display_name.trim() || sourceForm.tool_name.trim()}".
          <br /><a href={`/kb/doc/${spaceId}`}>Open Knowledge Space</a>
        </span>
      );
    } catch (e) {
      setError(e.message || "Failed to create live Knowledge Space.");
    } finally {
      setLoading(false);
    }
  };

  const resetToNewIndex = () => {
    setCreatedIndexId(null);
    setIndexDetails(null);
    setUploadId(null); // Reset upload ID for new index
    setResult(null);
    setError("");
    setName("");
    setDescription("");
    setUploadedFiles({});
    setSourcePathArray([]);
    setValidationError({});
    setActiveStep(-1);
    setUploadComplete(false);
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Knowledge Space Builder
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: colors.muted }}>
            Start with uploaded files or connect a live source backed by a governed Tool Forge query tool.
          </Typography>
        </Box>
      </Stack>

      <Paper
        component="form"
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${colors.border}`,
          bgcolor: colors.panel,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
          <Button
            type="button"
            onClick={() => {
              setSourceMode("documents");
              setError("");
              setResult(null);
            }}
            startIcon={<FolderOpenOutlinedIcon />}
            variant={sourceMode === "documents" ? "contained" : "outlined"}
            sx={modeButtonSx(sourceMode === "documents", colors, theme)}
          >
            Upload documents
          </Button>
          <Button
            type="button"
            onClick={() => {
              setSourceMode("live");
              setError("");
              setResult(null);
            }}
            startIcon={<ManageSearchIcon />}
            variant={sourceMode === "live" ? "contained" : "outlined"}
            sx={modeButtonSx(sourceMode === "live", colors, theme)}
          >
            Connect live source
          </Button>
        </Stack>

        <TextField 
          label="Collection Name" 
          value={name} 
          onChange={e => {setName(e.target.value); if (validationError) setValidationError({});}} 
          error={Boolean(validationError.name)}
          helperText={validationError.name}
          fullWidth
          sx={{ mb: 2, ...formControlSx(colors) }}
          disabled={createdIndexId !== null}
        />
        <TextField 
          label="Description (optional)" 
          value={description} 
          onChange={e => setDescription(e.target.value)} 
          fullWidth
          sx={{ mb: 2, ...formControlSx(colors) }}
          disabled={createdIndexId !== null}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 2 }}>
          <InfoOutlinedIcon fontSize="small" color="info" />
          <Typography variant="caption" color="text.secondary">
            {!name.trim()
              ? "Please provide a Knowledge Space name before adding sources."
              : sourceMode === "live"
                ? "Connect a published query tool as a live source. Agents will still call query_knowledge."
                : createdIndexId
                  ? "Upload more files to add them to the existing collection."
                  : "Upload files to create a document collection."}
          </Typography>
        </Box>

        {sourceMode === "documents" ? (
          <>
            <RAGFileUploader
              setUploadedFiles={setUploadedFiles}
              uploadId={uploadId}
              onUploadIdCreated={setUploadId}
            />

            {/* Progress indicator and stepper */}
            {activeStep >= 0 && (
          <Box sx={{ mt: 3 }}>
            {loading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {activeStep === 0 ? "Upload complete, processing..." : "Processing files..."}
                </Typography>
              </Box>
            )}
            
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((label, index) => (
                <Step key={label} expanded={true}>
                  <StepLabel>
                    {label}
                    {index < activeStep && (
                      <CheckCircle color="success" sx={{ ml: 1, fontSize: 20 }} />
                    )}
                  </StepLabel>
                  <StepContent>
                    {index === 0 && uploadComplete && (
                      <Typography variant="body2" color="text.secondary">
                        Files uploaded successfully. Ready to process.
                      </Typography>
                    )}
                    {index === 1 && activeStep > 1 && (
                      <Typography variant="body2" color="text.secondary">
                        {createdIndexId 
                          ? "Files were queued for processing. Open the collection status for live progress."
                          : "Collection was queued for processing. Open the collection status for live progress."}
                      </Typography>
                    )}
                    {index === 1 && activeStep === 1 && loading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Processing files...
                        </Typography>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              border: `1px solid ${colors.border}`,
              borderRadius: UI_RADIUS.panel,
              bgcolor: colors.panel,
            }}
          >
            <Stack spacing={1.5}>
              <TextField
                label="Source display name"
                placeholder="Aurivio Customer Data Lake"
                value={sourceForm.display_name}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, display_name: event.target.value }))}
                fullWidth
                sx={formControlSx(colors)}
              />
              <TextField
                select
                label="Source type"
                value={sourceForm.source_type}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, source_type: event.target.value }))}
                fullWidth
                sx={formControlSx(colors)}
              >
                <MenuItem value="tool_query">Tool-backed query</MenuItem>
                <MenuItem value="connector">Connector</MenuItem>
                <MenuItem value="database">Database</MenuItem>
                <MenuItem value="api">API</MenuItem>
              </TextField>
              <TextField
                required
                label="Published tool function name"
                placeholder="aurivio_data_lake_query"
                value={sourceForm.tool_name}
                onChange={(event) => {
                  setSourceForm((prev) => ({ ...prev, tool_name: event.target.value }));
                  if (validationError.tool_name) setValidationError((prev) => ({ ...prev, tool_name: undefined }));
                }}
                error={Boolean(validationError.tool_name)}
                helperText={validationError.tool_name || "Use the Tool Forge function name. The agent will see this as a Knowledge Source, not a direct action tool."}
                fullWidth
                sx={formControlSx(colors)}
              />
              <TextField
                label="Capability"
                placeholder="semantic_search"
                value={sourceForm.capability}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, capability: event.target.value }))}
                fullWidth
                sx={formControlSx(colors)}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ManageSearchIcon />}
                  disabled={loading || !name.trim() || !sourceForm.tool_name.trim() || createdIndexId !== null}
                  onClick={handleCreateLiveSource}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: UI_RADIUS.button,
                    px: 2.5,
                    py: 1.1,
                    fontWeight: 900,
                    textTransform: "none",
                    boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
                    "&:hover": { bgcolor: "primary.dark", boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}` },
                  }}
                >
                  {loading ? "Creating..." : "Create live Knowledge Space"}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
        
        {result && <Alert severity="success" sx={{ mt: 2 }}>{result}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {createdIndexId && (
          <Alert
            severity={sourceMode === "live" ? "success" : "info"}
            sx={{ mt: 2 }}
            action={
                <Button 
                  type="button"
                  color="inherit"
                  size="small"
                  onClick={resetToNewIndex}
                  sx={{ textTransform: 'none' }}
                >
                  Start New Space
                </Button>
            }
          >
            <Typography variant="body2" color="success.dark">
              {sourceMode === "live"
                ? `✓ Knowledge Space "${indexDetails?.name}" was created with a live source. Open it to review sources or attach it to agents.`
                : `✓ Collection "${indexDetails?.name}" was created and queued for processing. You can upload more files to add them to this collection.`}
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
